import { Box, Button, Collapse, useTheme } from '@mui/material';
import { ECInboxList } from './ECInboxList';
import { ECEmailsList } from './ECEmailsList';
import { ECEmailConversation } from './ECEmailConversation';
import { tokens } from '../../global/theme/tokens';
import { Fragment, useEffect, useRef, useState } from 'react';
import ExoDialog from '../exo/ExoDialog';
import { NewEmailIntegration } from '../new/NewEmailIntegration';
import { updateOrCreate } from '../special/updateOrCreate';
import useFetch from '../../api/useFetch';
import DeleteIcon from '@mui/icons-material/Delete';
import InboxIcon from '@mui/icons-material/Inbox';
import SendIcon from '@mui/icons-material/Send';
import BlockIcon from '@mui/icons-material/Block';
import DraftsIcon from '@mui/icons-material/Drafts';
import InventoryIcon from '@mui/icons-material/Inventory';
import { useTranslation } from 'react-i18next';
import { DBRequest, useConfig } from '../../api/api';
import { NewEmail } from '../new/NewEmail';
import ExoDrawer from '../exo/ExoDrawer';
import Div100vh from 'react-div-100vh';
import { ECSettings } from './ECSettings';
import { useEmailClient } from './useEmailClient';

export const EmailClient = ({ debug, emailId }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const { emailCredentials, setEmailCredentials } = useEmailClient();

  const emailListRef = useRef(null);
  const emailConversationRef = useRef(null);

  const [openNewEmail, setOpenNewEmail] = useState(null);

  const [openSettings, setOpenSettings] = useState(false);

  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(
    emailId ? { id: emailId } : null
  );

  // emailIntegrations
  function handleChangedEmailIntegration(res) {
    const newEmailIntegration = updateOrCreate(emailCredentials, res.data);

    setEmailCredentials(newEmailIntegration);
  }

  const folderMap = {
    Trash: {
      label: t('Trash'),
      Icon: DeleteIcon,
    },
    'Sent Items': {
      label: t('Sent Items'),
      Icon: SendIcon,
    },
    Spam: {
      label: t('Spam'),
      Icon: BlockIcon,
    },
    Drafts: {
      label: t('Drafts'),
      Icon: DraftsIcon,
    },
    Archive: {
      label: t('Archive'),
      Icon: InventoryIcon,
    },
    INBOX: {
      label: t('Inbox'),
      Icon: InboxIcon,
    },
  };

  function syncEmails() {
    DBRequest({
      config,
      path: `emails-sync`,
    });
  }

  useEffect(() => {
    // select first folder if no folder is selected
    if (emailCredentials && !selectedFolder) {
      const credentials = emailCredentials.data[0];
      if (!credentials) return;
      const folders = credentials.inbox.folders;
      const newSelectedFolder = folders.find((obj) => obj.name === 'INBOX');
      setSelectedFolder(newSelectedFolder);
    }
  }, [emailCredentials]);

  // new email
  const [editEmail, setEditEmail] = useState(null);

  function handleNewMail(newMail) {
    // postausgang is missing
    setEditEmail(false);
  }

  // handle email change for list and conversation
  function handleEmailChangeForList(email) {
    emailListRef.current.updateEmail(email.data);
  }

  function handleEmailChangeForConversation(email) {
    emailConversationRef.current.updateEmail(email.data);
  }

  // mobile
  const [currentView, setCurrentView] = useState('folder');

  return (
    <Fragment>
      <Box className="hidden lg:flex w-full gap-2 h-full justify-stretch">
        {debug && (
          <Button
            variant="contained"
            color="warning"
            sx={{ position: 'absolute' }}
            className="z-10 bottom-0 left-0"
            onClick={syncEmails}
          >
            sync
          </Button>
        )}
        <Box
          className="rounded-lg flex h-full overflow-hidden backdrop-blur-lg"
          sx={{
            bgcolor: colors.glass,
          }}
        >
          <ECInboxList
            onSelect={setSelectedFolder}
            selectedFolder={selectedFolder}
            onOpen={() => setOpenNewEmail(true)}
            inboxes={emailCredentials?.data}
            isLoading={!emailCredentials}
            onInboxChange={handleChangedEmailIntegration}
            folderMap={folderMap}
            width="200px"
            onOpenSettings={() => setOpenSettings(true)}
          />
          <Box sx={{ bgcolor: colors.card }} className="w-[1px]" />
          <ECEmailsList
            ref={emailListRef}
            inbox={selectedFolder}
            folderMap={folderMap}
            onSelect={setSelectedEmail}
            selectedEmail={selectedEmail}
            selectedFolder={selectedFolder}
            credentials={emailCredentials}
            onFolderChange={setSelectedFolder}
            onNewMail={() => setEditEmail(true)}
            noKeyAction={Boolean(editEmail)}
            onEmailChange={handleEmailChangeForConversation}
            onFolderSelect={setSelectedFolder}
          />
        </Box>
        <ECEmailConversation
          ref={emailConversationRef}
          selectedEmail={selectedEmail}
          onEdit={setEditEmail}
          onChange={handleEmailChangeForList}
        />
      </Box>
      <Div100vh className=" lg:hidden absolute top-0 pt-[64px] pb-[40px] left-0 w-full">
        <Box
          className="lg:hidden h-full sm:h-full flex w-full"
          sx={{
            '.MuiCollapse-wrapperInner, .MuiCollapse-wrapper, .MuiCollapse-root':
              {
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              },
          }}
        >
          {currentView === 'folder' && (
            <Box
              className="rounded-lg flex h-full overflow-hidden backdrop-blur-lg w-full"
              sx={{
                bgcolor: colors.glass,
              }}
            >
              <ECInboxList
                onSelect={(e) => {
                  setCurrentView('list');
                  setSelectedFolder(e);
                }}
                selectedFolder={selectedFolder}
                onOpen={() => setOpenNewEmail(true)}
                inboxes={emailCredentials?.data}
                folderMap={folderMap}
                isLoading={!emailCredentials}
                width="100%"
              />
            </Box>
          )}

          {(currentView === 'list' || currentView === 'email') && (
            <Box
              className="rounded-lg flex h-full overflow-hidden backdrop-blur-lg w-full"
              sx={{
                bgcolor: colors.glass,
              }}
            >
              <ECEmailsList
                ref={emailListRef}
                inbox={selectedFolder}
                folderMap={folderMap}
                onSelect={(e) => {
                  setCurrentView('email');
                  setSelectedEmail(e);
                }}
                selectedEmail={selectedEmail}
                selectedFolder={selectedFolder}
                credentials={emailCredentials}
                onFolderChange={setSelectedFolder}
                onNewMail={() => setEditEmail(true)}
                noKeyAction={Boolean(editEmail)}
                onEmailChange={handleEmailChangeForConversation}
                onFolderSelect={setSelectedFolder}
                isMobile
                onChangeView={setCurrentView}
              />
            </Box>
          )}
          <ExoDrawer
            open={currentView === 'email'}
            onClose={() => setCurrentView('list')}
          >
            <Box className="flex h-full overflow-hidden w-full">
              <ECEmailConversation
                ref={emailConversationRef}
                selectedEmail={selectedEmail}
                onEdit={setEditEmail}
                onChange={handleEmailChangeForList}
                onChangeView={setCurrentView}
              />
            </Box>
          </ExoDrawer>
        </Box>
      </Div100vh>
      <ExoDialog
        open={openNewEmail}
        onClose={() => setOpenNewEmail(false)}
        limitWidth
        maxWidth="550px"
      >
        <NewEmailIntegration
          onSelect={handleChangedEmailIntegration}
          onClose={() => setOpenNewEmail(false)}
        />
      </ExoDialog>
      <ExoDialog open={Boolean(editEmail)} onClose={() => setEditEmail(null)}>
        <NewEmail
          type={editEmail?.type}
          oldEmail={editEmail?.oldEmail}
          credentials={emailCredentials?.data}
          selectedFolder={selectedFolder}
          onNewMail={handleNewMail}
        />
      </ExoDialog>
      <ExoDialog
        open={openSettings}
        onClose={() => setOpenSettings(false)}
        limitWidth
      >
        <ECSettings
          onInboxChange={handleChangedEmailIntegration}
          credentials={emailCredentials}
          setCredentials={setEmailCredentials}
        />
      </ExoDialog>
    </Fragment>
  );
};
